import { createApi } from "@reduxjs/toolkit/query/react";
import { adminBaseQuery } from "@store/baseQuery";

// Type definitions based on swagger schema
export interface Speciality {
  id: number;
  title: string;
  is_shown: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateSpecialityRequest {
  title: string;
  is_shown: boolean;
}

export interface UpdateSpecialityRequest {
  title: string;
  is_shown: boolean;
}

export interface ErrorResponse {
  status: boolean;
  message: string;
  errors?: Record<string, string[]>;
}

export const specialitiesApi = createApi({
  reducerPath: "specialitiesApi",
  baseQuery: adminBaseQuery,
  tagTypes: ["Speciality"],
  endpoints: (builder) => ({
    // Get all specialities
    getSpecialities: builder.query<Speciality[], void>({
      query: () => "/specialities",
      providesTags: ["Speciality"],
    }),

    // Get a specific speciality
    getSpeciality: builder.query<Speciality, number>({
      query: (id) => `/specialities/${id}`,
      providesTags: (result, error, id) => [
        { type: "Speciality", id },
      ],
    }),

    // Create a new speciality
    createSpeciality: builder.mutation<
      Speciality,
      CreateSpecialityRequest
    >({
      query: (data) => ({
        url: "/specialities",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Speciality"],
    }),

    // Update a specific speciality
    updateSpeciality: builder.mutation<
      Speciality,
      { id: number; data: UpdateSpecialityRequest }
    >({
      query: ({ id, data }) => ({
        url: `/specialities/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Speciality", id },
        "Speciality",
      ],
    }),

    // Delete a specific speciality
    deleteSpeciality: builder.mutation<
      { speciality_id: number },
      number
    >({
      query: (id) => ({
        url: `/specialities/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Speciality"],
    }),
  }),
});

export const {
  useGetSpecialitiesQuery,
  useGetSpecialityQuery,
  useCreateSpecialityMutation,
  useUpdateSpecialityMutation,
  useDeleteSpecialityMutation,
} = specialitiesApi; 